import { MessageDescriptor } from '@redbubble/next/intl';
import {
  callback,
  PayPalCheckout as PayPalCheckoutIncomplete,
} from 'braintree-web';
import type { PayPalCheckoutUpdatePaymentServerDataReturn } from 'braintree-web/paypal-checkout';

import type {
  AuthorizationData,
  AuthorizationResponse,
  AuthorizationResponseDetails,
  ButtonShapeOption,
  ButtonsRenderer as ButtonsRendererIncomplete,
  FlowType,
  Intent,
  LineItem,
  LineItemKind,
  Address as PaypalSdkAddress,
  ShippingOption,
  ShippingOptionType,
} from 'paypal-checkout-components';
import { ShippingMethod } from '../../types/cart/ShippingMethod';
import { AppliedCoupon, Cart, Item } from '../../types/monolith/cart';

// Re-export PayPal typedefs so we can use this file as the single source of truth.
// It helps to keep all the type gymnastics in one location.
export {
  AuthorizationData,
  AuthorizationResponse,
  AuthorizationResponseDetails,
  ButtonShapeOption,
  FlowType,
  Intent,
  LineItem,
  LineItemKind,
  PayPalCheckoutUpdatePaymentServerDataReturn,
  PaypalSdkAddress,
  ShippingOption,
  ShippingOptionType,
};

export interface PayPalRequiredCart {
  summary: Pick<
    Cart['summary'],
    'subtotal' | 'total' | 'tax' | 'show_tax_separately'
  > & {
    estimated_shipping: Pick<
      Cart['summary']['estimated_shipping'],
      'preference' | 'standard' | 'standard_ex_tax'
    > & {
      express: Cart['summary']['estimated_shipping']['express'] | null;
      express_ex_tax:
        | Cart['summary']['estimated_shipping']['express_ex_tax']
        | null;
    };
  };
  items: Array<Pick<Item, 'quantity'>>;
  applied_coupon: Pick<AppliedCoupon, 'amount' | 'type' | 'code'> | null;
}

export interface PayError {
  code: string;
  message?: string;
  title?: string;
}
export type ErrorMessage = MessageDescriptor | string;
export type PayPalErrorCallback = (message?: ErrorMessage) => void;

export interface ShippingAddressChangeData {
  errors: Record<string, string>[];
  orderID: string;
  shippingAddress: {
    city: string | null;
    countryCode: string;
    postalCode: string | null;
    state: string | null;
  };
}

export interface ShippingOptionsChangeData {
  errors: Record<string, string>[];
  orderID: string;
  selectedShippingOption: {
    id: ShippingMethod.Standard | ShippingMethod.Express;
    label: string;
    selected: boolean;
    type: 'SHIPPING' | 'PICKUP';
    amount: {
      currencyCode: string;
      value: string;
    };
  };
}

export interface PayPalCheckoutUpdatePaymentOptions {
  paymentId: string;
  amount: string | number;
  currency: string;
  shippingOptions?: ShippingOption[];
  lineItems: LineItem[];
}

// The provided PayPalCheckout type lacks a definition for updatePayment despite being clearly
// documented in https://braintree.github.io/braintree-web/current/PayPalCheckout.html#updatePayment
// so we are manually extending the type here and using it everywhere.
// UPGRADE: paypal-checkout-components we can remove this when the type def is updated
export interface PayPalCheckout extends PayPalCheckoutIncomplete {
  updatePayment(
    options: PayPalCheckoutUpdatePaymentOptions,
    callback?: callback
  ): Promise<PayPalCheckoutUpdatePaymentServerDataReturn>;
}

// The provided ButtonsRenderer lacks definitions for the two new "onShippingAddressChange" and
// "onShippingOptionsChange" callbacks that have since been split out from the original "combined"
// "onShippingChange" callback, so once again we are extending the type here and using it everywhere.
// https://developer.paypal.com/sdk/js/reference/#link-onshippingaddresschange
// https://developer.paypal.com/sdk/js/reference/#link-onshippingoptionschange
// UPGRADE: paypal-checkout-components we can remove this when the type def is updated
type ButtonsRendererOptions = Parameters<ButtonsRendererIncomplete>[0];
export interface ButtonsRenderer extends ButtonsRendererIncomplete {
  (
    options: ButtonsRendererOptions & {
      onShippingAddressChange?(data: ShippingAddressChangeData): Promise<void>;
      onShippingOptionsChange?(data: ShippingOptionsChangeData): Promise<void>;
    }
  ): ButtonsRenderer;
}

// page type needs to match monolith's https://github.com/redbubble/redbubble/blob/master/app/api/redbubble/api/checkout/braintree/paypal.rb#L23-L26
// it determines the payment_type for a purchase order
export type PageType = 'cart' | 'check-out' | 'cart-tray';
